import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { AdminServiceOfferRepository } from './domain/AdminServiceOfferRepository'

import {
  AdminServiceOfferDetails,
  LocalizedGroupServiceOfferView,
  MetaTags,
  ModificationRequest,
} from '../../contracts/service-offer'
import { ExtraPriceListItem } from '../../contracts/service-offer/AdminServiceOfferExtraPrice'
import { DuplicatedEventStatus, DuplicateRequest } from '../../contracts/service-offer/DuplicateRequest'
import { AxiosErrorHandler, PaginationCriteria } from '../../foundation'
import { QnaItem } from '../../contracts/service-offer'
import { SupernovaPagedBody } from '../../interfaces/ApiServiceResponse'

export class AdminApiServiceOffer implements AdminServiceOfferRepository {
  private errorHandler: AxiosErrorHandler

  constructor(private axios: AxiosInstance) {
    this.errorHandler = new AxiosErrorHandler()
  }

  getById = async (id: number): Promise<AdminServiceOfferDetails> => {
    const config: AxiosRequestConfig<{ id: number }> = {
      url: '/service-offer/query/admin/get-service-offer/process',
      method: 'post',
      data: { id },
    }

    try {
      const { data } = await this.axios.request<AdminServiceOfferDetails>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getByIds = async (ids: number[]): Promise<{ id: number; title: string }[]> => {
    const config: AxiosRequestConfig<{ ids: number[] }> = {
      url: '/service-offer/query/admin/get-service-offers/process',
      method: 'post',
      data: { ids },
    }

    try {
      const { data } = await this.axios.request<{ id: number; title: string }[]>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  modify = async (command: ModificationRequest & { hospitalId: number }): Promise<void> => {
    const config: AxiosRequestConfig<ModificationRequest & { hospitalId: number }> = {
      url: '/service-offer/command/admin/modify-service-offer/execute',
      method: 'post',
      data: { ...command },
    }

    try {
      await this.axios.request<AdminServiceOfferDetails>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  postMemo = async (serviceOfferId: number, memo: string): Promise<void> => {
    const config: AxiosRequestConfig<{ serviceOfferId: number; memo: string }> = {
      url: '/service-offer/command/admin/post-service-offer-memo/execute',
      method: 'post',
      data: { serviceOfferId, memo },
    }

    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  modifyMetaTags = async (serviceOfferId: number, metaTags: MetaTags): Promise<void> => {
    const config: AxiosRequestConfig<{ serviceOfferId: number } & MetaTags> = {
      url: '/service-offer/command/admin/modify-meta-tags/execute',
      method: 'post',
      data: { serviceOfferId, ...metaTags },
    }

    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  modifyAdDeductionPoint = async (serviceOfferId: number, adDeductionPoint: number): Promise<void> => {
    const config: AxiosRequestConfig<{ serviceOfferId: number; adDeductionPoint: number }> = {
      url: '/service-offer/command/admin/modify-ad-deduction-point/execute',
      method: 'post',
      data: { serviceOfferId, adDeductionPoint },
    }

    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  activate = async (serviceOfferId: number): Promise<void> => {
    const config: AxiosRequestConfig<{ serviceOfferId: number }> = {
      url: '/service-offer/command/admin/activate/execute',
      method: 'post',
      data: { serviceOfferId },
    }

    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  deactivate = async (serviceOfferId: number): Promise<void> => {
    const config: AxiosRequestConfig<{ serviceOfferId: number }> = {
      url: '/service-offer/command/admin/deactivate/execute',
      method: 'post',
      data: { serviceOfferId },
    }

    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  connectGroup = async (command: { groupId: string; serviceOfferId: number }): Promise<void> => {
    const config: AxiosRequestConfig<{ groupId: string; serviceOfferId: number }> = {
      url: '/service-offer/command/admin/connect-group/execute',
      method: 'post',
      data: command,
    }

    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  disconnectGroup = async (command: { groupId: string; serviceOfferId: number }): Promise<void> => {
    const config: AxiosRequestConfig<{ groupId: string; serviceOfferId: number }> = {
      url: '/service-offer/command/admin/disconnect-group/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  search = async (command: { title: string; hospitalId: number }): Promise<LocalizedGroupServiceOfferView[]> => {
    const config: AxiosRequestConfig<{ title: string; hospitalId: number }> = {
      url: '/service-offer/query/admin/groups/process',
      method: 'post',
      data: command,
    }
    try {
      const { data } = await this.axios.request<LocalizedGroupServiceOfferView[]>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  duplicate = async (
    serviceOfferIds: number[],
    hospitalIds: number[],
    serviceOfferStatus: DuplicatedEventStatus,
  ): Promise<void> => {
    const config: AxiosRequestConfig<DuplicateRequest> = {
      url: '/service-offer/command/admin/create-replication-requests/execute',
      method: 'post',
      data: { source: { serviceOfferIds }, destination: { hospitalIds, payload: { serviceOfferStatus } } },
    }
    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getExtraPriceList = async (hospitalCountry: string): Promise<ExtraPriceListItem[]> => {
    const config: AxiosRequestConfig<{ hospitalCountry: string }> = {
      url: '/extra-price/query/partner/all-extra-prices/process',
      method: 'post',
      data: { hospitalCountry },
    }

    try {
      const { data } = await this.axios.request<ExtraPriceListItem[]>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getExtraPriceListForAdmin = async (hospitalCountry: string): Promise<ExtraPriceListItem[]> => {
    const config: AxiosRequestConfig<{ hospitalCountry: string }> = {
      url: '/extra-price/query/admin/all-extra-prices/process',
      method: 'post',
      data: { hospitalCountry },
    }

    try {
      const { data } = await this.axios.request<ExtraPriceListItem[]>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getQnaList = async (
    qnaCriteria: PaginationCriteria<{ hospitalId?: number }>,
  ): Promise<SupernovaPagedBody<QnaItem[]>> => {
    const config: AxiosRequestConfig<PaginationCriteria<{ hospitalId?: number }>> = {
      url: '/service-offer/qna/queries/admin/get-questions-and-answers/process',
      method: 'post',
      data: qnaCriteria,
    }

    try {
      const { data } = await this.axios.request<SupernovaPagedBody<QnaItem[]>>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  deleteQna = async (questionId: number): Promise<void> => {
    const config: AxiosRequestConfig<{ questionId: number }> = {
      url: '/service-offer/qna/commands/admin/remove-question/execute',
      method: 'post',
      data: { questionId },
    }

    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }
}
